/**
 * The player's combat input in the loop (plan G14, spec §9): per tick the whole `PlayerControl` (flight, boost,
 * primary and torpedo fire) goes into `Control`; per frame the weapon-select and target edge actions go to the
 * G2/G3 functions. A test-build fire hold (`__debug.holdFire`) can add fire for a number of ticks.
 * Everything here runs in the hot loop and allocates nothing (D-014).
 */
import { createTargetPick, targetAction, type TargetPick } from '../combat/targeting'
import { cyclePrimary, selectPrimary } from '../combat/weapons'
import { SIM_DT } from '../core/loop'
import { drainPressed, type InputDevices } from '../input/devices'
import type { PlayerControl } from '../input/playerInput'
import type { Sim } from '../sim'
import { Control, Life, Ship } from '../world/components'

/** Ticks of forced fire left (test builds). */
export interface FireHold {
  primaryTicks: number
  secondaryTicks: number
}

export function createFireHold(): FireHold {
  return { primaryTicks: 0, secondaryTicks: 0 }
}

/** Holds primary and/or secondary fire for `ms` of sim time; a `false` releases that trigger at once. */
export function holdFire(hold: FireHold, primary: boolean, secondary: boolean, ms: number): void {
  const ticks = Math.max(0, Math.ceil(ms / (SIM_DT * 1000) - 1e-9))
  hold.primaryTicks = primary ? ticks : 0
  hold.secondaryTicks = secondary ? ticks : 0
}

/** One tick: the player's control frame into `Control` (fire held by the player or by the hold). */
export function writePlayerControl(eid: number, c: PlayerControl, hold: FireHold): void {
  Control.surge[eid] = c.surge
  Control.sway[eid] = c.sway
  Control.heave[eid] = c.heave
  Control.pitch[eid] = c.pitch
  Control.yaw[eid] = c.yaw
  Control.roll[eid] = c.roll
  Control.boost[eid] = c.boost ? 1 : 0
  Control.firePrimary[eid] = c.firePrimary || hold.primaryTicks > 0 ? 1 : 0
  Control.fireSecondary[eid] = c.fireSecondary || hold.secondaryTicks > 0 ? 1 : 0
  if (hold.primaryTicks > 0) hold.primaryTicks--
  if (hold.secondaryTicks > 0) hold.secondaryTicks--
}

/** The combat edge actions of one frame. */
export interface CombatEdges {
  selectPrimary1: boolean
  selectPrimary2: boolean
  cyclePrimary: boolean
  target: boolean
  /** Scratch for the targeting functions. */
  readonly pick: TargetPick
}

export function createCombatEdges(): CombatEdges {
  return { selectPrimary1: false, selectPrimary2: false, cyclePrimary: false, target: false, pick: createTargetPick() }
}

/** Drains this frame's combat edge actions from the input devices. */
export function drainCombatEdges(input: InputDevices, edges: CombatEdges): void {
  edges.selectPrimary1 = drainPressed(input, 'selectPrimary1')
  edges.selectPrimary2 = drainPressed(input, 'selectPrimary2')
  edges.cyclePrimary = drainPressed(input, 'cyclePrimary')
  edges.target = drainPressed(input, 'target')
}

/** Applies the edges to the living player: keys 1/2 select a primary slot, pad X cycles, T / pad Y targets. */
export function applyCombatEdges(sim: Sim, edges: CombatEdges): void {
  const p = sim.playerEid
  if (p < 0 || Life.alive[p] !== 1) return
  const def = sim.defs[Ship.defIndex[p]]
  if (edges.selectPrimary1) selectPrimary(p, 0, def)
  if (edges.selectPrimary2) selectPrimary(p, 1, def)
  if (edges.cyclePrimary) cyclePrimary(p, def)
  if (edges.target) targetAction(sim.ships.dense, p, edges.pick)
}
